import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Chip,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme,
    Divider
} from '@mui/material';
import { Close, Work as WorkIcon, LocationOn, Schedule, AttachMoney, CheckCircle, Cancel } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { JobPosting } from '../../services/jobPostingApi';

interface JobPostingViewProps {
    open: boolean;
    jobPosting: JobPosting | null;
    onClose: () => void;
}

const JobPostingView: React.FC<JobPostingViewProps> = ({ open, jobPosting, onClose }) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (!jobPosting) return null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    minHeight: isMobile ? '100vh' : 'auto'
                }
            }}
        >
            <DialogTitle sx={{ 
                p: isMobile ? 2 : 3, 
                pb: isMobile ? 1 : 2,
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WorkIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Job Posting Details
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 3 }}>
                {/* Header Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                        {jobPosting.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                            label={jobPosting.is_active ? 'Active' : 'Inactive'}
                            color={jobPosting.is_active ? 'success' : 'default'}
                            icon={jobPosting.is_active ? <CheckCircle /> : <Cancel />}
                            size="small"
                        />
                        {jobPosting.job_type && (
                            <Chip
                                label={jobPosting.job_type}
                                variant="outlined"
                                size="small"
                            />
                        )}
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {jobPosting.location && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="textSecondary">
                                        {jobPosting.location}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {jobPosting.job_type && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="textSecondary">
                                        {jobPosting.job_type}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {jobPosting.salary && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="textSecondary">
                                        {jobPosting.salary}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {jobPosting.required_experience && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WorkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="textSecondary">
                                        {jobPosting.required_experience}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Description Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                        Job Description
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {jobPosting.description}
                    </Typography>
                </Box>

                {/* Requirements Section */}
                {jobPosting.requirements && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                            Requirements
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {jobPosting.requirements}
                        </Typography>
                    </Box>
                )}

                {/* Responsibilities Section */}
                {jobPosting.responsibilities && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                            Responsibilities
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {jobPosting.responsibilities}
                        </Typography>
                    </Box>
                )}

                {/* Benefits Section */}
                {jobPosting.benefits && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                            Benefits
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {jobPosting.benefits}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Additional Information */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                        Additional Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Applications Received
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {jobPosting.careers_count || 0}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Posted On
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(jobPosting.created_at)}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Last Updated
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(jobPosting.updated_at)}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Status
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {jobPosting.is_active ? 'Active' : 'Inactive'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ 
                p: isMobile ? 2 : 3, 
                borderTop: `1px solid ${theme.palette.divider}`,
                gap: 1,
                flexDirection: isMobile ? 'column' : 'row'
            }}>
                <Button
                    onClick={onClose}
                    fullWidth={isMobile}
                    sx={{ 
                        color: theme.palette.text.secondary,
                        borderRadius: 3
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobPostingView;
